import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FaUserCog, FaDownload } from 'react-icons/fa';
import { RiArrowGoBackLine, RiLogoutCircleRLine } from 'react-icons/ri';
import Swal from 'sweetalert2';
import axios from 'axios';
import Logo from '../ATH_Full_White.png';
import { useAppContext } from '../context/AppContext';

const HeaderContainer = styled.div`
  height: 120px;
  background-color: ${props => props.theme.colors.background};
  padding-left: ${props => props.theme.spacing.large};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
`;

const LogoImage = styled.img`
  height: 60px;
  align-self: center;
  margin-left: -20px;
`;

const HeaderUserInfo = styled.div`
  display: flex;
  margin-right: ${props => props.theme.spacing.small};
`;

const HeaderButton = styled.div`
  color: ${props => props.theme.colors.primary};
  background-color: ${props => props.theme.colors.background};
  font-size: ${props => props.theme.fontSizes.large};
  margin-left: ${props => props.theme.spacing.large};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`;

const ButtonText = styled.p`
  margin: ${props => props.theme.spacing.small} 0 0 0;
  font-size: ${props => props.theme.fontSizes.small};
`;

const HeaderComponent = () => {
  const { 
    user, 
    setUser, 
    view,
    setView, 
  } = useAppContext();

  const handleLogout = useCallback(() => {
    setUser(null);
    setView('login');
  }, [setUser, setView]);

  const handlePageChange = useCallback((page) => {
    setView(page);
  }, [setView]);

  const exportDatabase = useCallback(async () => {
    try {
      Swal.fire({
        title: 'Exporting database',
        text: 'Please wait. This may take a few minutes.',
        didOpen: () => { Swal.showLoading() },
        allowOutsideClick: false,
      });
  
      const response = await axios.post(`${process.env.REACT_APP_API_URL}api/flask`, {
        functionName: 'ExportDatabase',
        inputArray: []
      });
  
      Swal.close();
  
      if (response.data) {
        const filePath = response.data;
        const fileName = filePath.split('\\').pop().split('/').pop();
        const getURLString = `${process.env.REACT_APP_API_URL}download?filePath=${encodeURIComponent(filePath)}`;
  
        const res = await axios.get(getURLString, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        link.click();
      }
    } catch (error) {
      Swal.close();
      Swal.fire('Error', 'Failed to export database', 'error');
      console.error('Export error:', error);
    }
  }, []);

  const renderHeaderContent = useMemo(() => {
    if (view === 'capture') {
      return (
        <>
          {user?.role === 'admin' && (
            <HeaderButton onClick={exportDatabase}>
              <FaDownload />
              <ButtonText>Export database</ButtonText>
            </HeaderButton>
          )}
          <HeaderButton onClick={() => handlePageChange('coachAdmin')}>
            <FaUserCog />
            <ButtonText>{user.firstName}</ButtonText>
          </HeaderButton>
          <HeaderButton onClick={handleLogout}>
            <RiLogoutCircleRLine />
            <ButtonText>Logout</ButtonText>
          </HeaderButton>
        </>
      );
    } else {
      return (
        <HeaderButton onClick={() => handlePageChange('capture')}>
          <RiArrowGoBackLine />
          <ButtonText>Back</ButtonText>
        </HeaderButton>
      );
    }
  }, [view, user, handlePageChange, handleLogout, exportDatabase]);

  return (
    <HeaderContainer>
      <Header>
        <LogoImage src={Logo} alt="Logo" />
        <HeaderUserInfo>
          {renderHeaderContent}
        </HeaderUserInfo>
      </Header>
    </HeaderContainer>
  );
};

export default React.memo(HeaderComponent);